import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import {
  addViewRegion,
  MAX_VIEW_REGIONS,
  readTaskPageDraft,
  startTaskPage,
  viewHasPathIssues,
  writeTaskPageDraft,
  type ViewSchemaSource,
} from './taskTypeViewModel';
import { TaskTypeViewRegionCard } from './TaskTypeViewRegionCard';
import { TaskTypeViewJsonPanel } from './TaskTypeViewJsonPanel';

export function TaskTypeViewBuilder({
  viewText,
  schema,
  disabled,
  onChange,
}: {
  viewText: string;
  schema: ViewSchemaSource;
  disabled?: boolean;
  onChange: (viewText: string) => void;
}) {
  const gt = useGT();
  const draft = readTaskPageDraft(viewText);
  const [confirmStandard, setConfirmStandard] = useState(false);
  const fields = schema.status === 'ready' ? schema.fields : [];
  const fieldsReady = schema.status === 'ready';
  const canDesign = schema.status === 'ready' || schema.status === 'none';

  function commitView(next: ReturnType<typeof startTaskPage>) {
    onChange(writeTaskPageDraft(next));
  }

  function applyStandardReview() {
    setConfirmStandard(false);
    onChange('');
  }

  if (draft.status === 'invalid') {
    return (
      <div
        className="flex flex-col items-start gap-2"
        data-testid="task-type-view-builder"
      >
        <p className="text-sm text-muted-foreground">
          <T context="task page builder when the saved layout cannot be edited">
            This layout is saved in a shape the editor cannot open. Fix the JSON
            below and apply it, or discard the layout.
          </T>
        </p>
        <div className="w-full">
          <TaskTypeViewJsonPanel
            viewText={viewText}
            disabled={disabled}
            defaultOpen
            onChange={onChange}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={applyStandardReview}
        >
          <T context="task page builder, throw away an unreadable layout">
            Discard Layout
          </T>
        </Button>
      </div>
    );
  }

  if (draft.status === 'standard') {
    return (
      <div
        className="flex flex-col items-start gap-2"
        data-testid="task-type-view-builder"
      >
        <SchemaNotice schema={schema} />
        <p className="text-sm text-muted-foreground">
          <T context="task page builder empty state">
            Standard review. The task page shows the description, subtasks, and
            the raw input.
          </T>
        </p>
        <Button
          type="button"
          size="sm"
          disabled={disabled || !canDesign}
          onClick={() => commitView(startTaskPage())}
        >
          <T context="task page builder, start laying out the task page">
            Design Task Page
          </T>
        </Button>
      </div>
    );
  }

  const view = draft.view;
  const hasPlacements = view.regions.some(
    (region) => region.placements.length > 0,
  );

  return (
    <div className="flex flex-col gap-3" data-testid="task-type-view-builder">
      <SchemaNotice schema={schema} />
      {(fieldsReady || schema.status === 'none') &&
      viewHasPathIssues(view, fields) ? (
        <p className="text-sm text-destructive" role="alert">
          <T context="task page builder when a placed field is not on the schema">
            Some items point at fields that are not on the input schema. Save
            will fail until they are fixed.
          </T>
        </p>
      ) : null}
      {view.regions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="task page builder when every section was removed">
            No sections yet. An empty layout hides the raw input on the task
            page.
          </T>
        </p>
      ) : (
        view.regions.map((region, index) => (
          <TaskTypeViewRegionCard
            key={region.id}
            view={view}
            region={region}
            index={index}
            count={view.regions.length}
            fields={fields}
            fieldsReady={fieldsReady}
            disabled={disabled}
            onCommit={commitView}
          />
        ))
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || view.regions.length >= MAX_VIEW_REGIONS}
          onClick={() => {
            const next = addViewRegion(view);
            if (next) commitView(next);
          }}
        >
          <PlusIcon />
          <T context="task page builder, add a section of the task page">
            Add Section
          </T>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => {
            if (hasPlacements) {
              setConfirmStandard(true);
              return;
            }
            applyStandardReview();
          }}
        >
          <T context="task page builder, drop the custom layout">
            Use Standard Review
          </T>
        </Button>
      </div>
      <TaskTypeViewJsonPanel
        viewText={viewText}
        disabled={disabled}
        onChange={onChange}
      />
      <ConfirmDialog
        open={confirmStandard}
        onClose={() => setConfirmStandard(false)}
        onConfirm={applyStandardReview}
        title={gt('Use the standard review?', {
          context: 'confirm discarding a designed task page',
        })}
        message={gt(
          'This removes the task page layout. You can design it again before saving.',
          { context: 'confirm discarding a designed task page' },
        )}
        confirmText={gt('Use Standard Review', {
          context: 'confirm button, discard the designed task page',
        })}
        dangerous
      />
    </div>
  );
}

function SchemaNotice({ schema }: { schema: ViewSchemaSource }) {
  if (schema.status === 'loading') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="task page builder while the input schema loads">
          Loading schema fields…
        </T>
      </p>
    );
  }
  if (schema.status === 'error') {
    return (
      <p className="text-sm text-destructive" role="alert">
        <T context="task page builder when the input schema failed to load">
          The input schema could not be loaded.
        </T>
      </p>
    );
  }
  if (schema.status === 'none') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="task page builder when the task type has no input schema">
          This type has no input schema. You can still place an email, document,
          or AP invoice viewer from the task anchor. Field, match, and line
          placements need an input schema.
        </T>
      </p>
    );
  }
  return null;
}
